import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { Tooltip } from 'flowbite-react';
import { type ReactNode } from 'react';
import { Icon } from '#common/components/Icon.tsx';
import { renderDataAttributes, type PropsWithDataAttributes } from '#common/components/types.ts';
import { type SupportedCurrency } from '#common/config/currencies';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { getMediumExamples, getMediumTitle } from '#common/medium-display';
import { CurrencySwitcher } from '#speaker-shop/components/CurrencySwitcher.tsx';
import { type ResolvedAvailableUse } from '#speaker-shop/components/shop-layouts/types.ts';
import { Tile } from '#speaker-shop/components/Tile.tsx';
import { Feature } from './Feature';
import { SectionTitle } from './SectionTitle';

interface Props {
    selectedMediums: string[];
    onSelectedMediumChange: (selectedMedium: string) => void;
    availableMediums: (ResolvedAvailableUse | {
        id: string;
        icon: string;
        title: ReactNode;
        subTitle: ReactNode;
        children?: ReactNode;
    })[];
    currency?: SupportedCurrency;
    availableCurrencies?: SupportedCurrency[];
    locale?: string;
}

export function MediumSelection(
    { selectedMediums, availableMediums, onSelectedMediumChange, currency, availableCurrencies, locale = 'en' }: Props,
) {
    const { t } = useEditableTranslation('medium-selection');

    return (
        <div className='py-8'>
            <div className='flex items-center justify-between'>
                <SectionTitle text={t('header')} color='blue' className='text-center md:text-left mb-0!' />
                {currency && availableCurrencies && (
                    <CurrencySwitcher
                        currency={currency}
                        availableCurrencies={availableCurrencies}
                        className='text-crispybluedark-700 text-base font-semibold'
                    />
                )}
            </div>
            <div className='hidden w-full gap-x-6 md:flex flex-row overflow-x-scroll px-6 pb-12 pt-6'>
                {availableMediums.map(x => {
                    let icon: string;
                    let title: ReactNode;
                    let subTitle: ReactNode;
                    let children: ReactNode | null;
                    let info: ReactNode | undefined;
                    const isSelected = selectedMediums.indexOf(x.id) !== -1;
                    const dataAttributes: Record<string, string> = {
                        selected: isSelected ? 'true' : 'false'
                    };
                    if ('icon' in x) {
                        icon = x.icon;
                        title = x.title;
                        subTitle = x.subTitle;
                        children = x.children;
                    } else {
                        icon = x.medium.icon;
                        title = getMediumTitle(x.medium, locale);
                        info = getMediumExamples(x.medium, locale);
                        subTitle = x.basePrice > 0
                            ? t('price-from', { currency: x.currency, basePrice: x.basePrice.toFixed(2) })
                            : t('price-free');
                        dataAttributes['medium-name'] = x.medium.name;
                        if (x.basePrice > 0) {
                            dataAttributes['medium-price'] = x.basePrice.toString();
                        }
                        children = (
                            <div className='flex flex-col gap-y-2 text-sm lg:text-base'>
                                {x.basePrice > 0
                                    ? (
                                        <>
                                            <Feature
                                                text={t('included-characters', { included: x.basePriceCharacters })}
                                            />
                                            <Feature
                                                text={t('price-additional-characters', {
                                                    price: (x.basePrice / x.basePriceCharacters).toFixed(2),
                                                    currency: x.currency
                                                })}
                                            />
                                        </>
                                    )
                                    : null}
                                {x.timeframeUnit
                                    ? (
                                        <Feature
                                            text={t('restricted-usage', {
                                                value: x.timeframeValue || 1,
                                                unit: x.timeframeUnit
                                            })}
                                        />
                                    )
                                    : <Feature text={t('unrestricted-usage')} />}
                            </div>
                        );
                    }

                    return (
                        <Tile
                            key={x.id}
                            icon={icon}
                            info={info}
                            title={title}
                            subTitle={subTitle}
                            className='shrink-0'
                            isSelected={isSelected}
                            onClick={() => onSelectedMediumChange(x.id)}
                            sizeClassNames={'w-60 h-60 lg:w-64 lg:h-64'}
                            dataAttributes={dataAttributes}
                        >
                            {children}
                        </Tile>
                    );
                })}
            </div>
            <div className='flex md:hidden flex-col rounded-lg overflow-hidden gap-0.5'>
                {availableMediums.map(x => {
                    let icon: string;
                    let title: ReactNode;
                    let subTitle: ReactNode;
                    let info: ReactNode | undefined;
                    const isSelected = selectedMediums.indexOf(x.id) !== -1;
                    const dataAttributes: Record<string, string> = {
                        selected: isSelected ? 'true' : 'false'
                    };
                    if ('icon' in x) {
                        icon = x.icon;
                        title = x.title;
                        subTitle = x.subTitle;
                    } else {
                        icon = x.medium.icon;
                        title = getMediumTitle(x.medium, locale);
                        dataAttributes['medium-name'] = x.medium.name;
                        if (x.basePrice > 0) {
                            dataAttributes['medium-price'] = x.basePrice.toString();
                        }
                        info = (
                            <>
                                {getMediumExamples(x.medium, locale)}
                                <div className='flex flex-col gap-y-2 text-sm lg:text-base'>
                                    <Feature text={t('included-characters', { included: x.basePriceCharacters })} />
                                    <Feature
                                        text={t('price-additional-characters', {
                                            price: (x.basePrice / x.basePriceCharacters).toFixed(2),
                                            currency: x.currency
                                        })}
                                    />
                                    {x.timeframeUnit
                                        ? (
                                            <Feature
                                                text={t('restricted-usage', {
                                                    value: x.timeframeValue || 1,
                                                    unit: x.timeframeUnit
                                                })}
                                            />
                                        )
                                        : <Feature text={t('unrestricted-usage')} />}
                                </div>
                            </>
                        );
                        subTitle = t('price-from', { currency: x.currency, basePrice: x.basePrice.toFixed(2) });
                    }

                    return (
                        <MobileMedium
                            key={x.id}
                            icon={icon}
                            info={info}
                            title={title}
                            subTitle={subTitle}
                            isSelected={isSelected}
                            onClick={() => onSelectedMediumChange(x.id)}
                            dataAttributes={dataAttributes}
                        />
                    );
                })}
            </div>
        </div>
    );
}

interface MobileMediumProps {
    icon: string;
    info?: ReactNode | string;
    title: ReactNode | string;
    subTitle: ReactNode | string;
    isSelected: boolean;
    onClick: () => void;
    className?: string;
}

export function MobileMedium({
    icon,
    info,
    title,
    subTitle,
    isSelected,
    onClick,
    dataAttributes,
}: PropsWithDataAttributes<MobileMediumProps>) {
    return (
        <div
            className={clsx(
                'flex flex-row gap-x-4 p-2 items-center',
                isSelected ? 'bg-crispybluedark-600 text-white' : 'bg-white text-crispybluedark-600'
            )}
            onClick={onClick}
            {...renderDataAttributes(dataAttributes)}
        >
            <Icon icon={icon} className='shrink-0' />
            <div className='mr-auto'>{title}</div>
            <p className=''>{subTitle}</p>
            {info && (
                <div onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                        content={<div>{info}</div>}
                        arrow={false}
                        placement='left'
                        trigger='click'
                        className='max-w-60 xs:max-w-none'
                    >
                        <InformationCircleIcon
                            className={clsx(
                                'cursor-help shrink-0 w-6 h-6',
                                isSelected ? 'text-white' : 'text-crispybluedark-600'
                            )}
                        />
                    </Tooltip>
                </div>
            )}
        </div>
    );
}
